import React, {useEffect} from 'react';
import {App, ConfigProvider, Spin} from 'antd';
import './App.css';
import {createBrowserRouter, RouterProvider, useNavigate, useLocation} from "react-router-dom";
import NotFound from "./NotFound";
import Sorteggio from "./Pages/Sorteggio";
import Sorteggi from "./Pages/Sorteggi";
import Landing from "./Pages/Landing";
import PrivacyPolicy from "./Pages/PrivacyPolicy";
import TermsOfService from "./Pages/TermsOfService";
import CookiePolicy from "./Pages/CookiePolicy";
import PublicAddPlayer from "./Pages/PublicAddPlayer";
import Reveal from "./Pages/Reveal";
import {useAuth0} from "@auth0/auth0-react";
import WebViewBanner from "./Components/WebViewBanner";
import GoogleAnalytics from "./Components/GoogleAnalytics";

// Component to protect authenticated routes
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
    const navigate = useNavigate();
    const location = useLocation();

    // Handle redirect after Auth0 login
    useEffect(() => {
        if (!isLoading && isAuthenticated) {
            const returnTo = sessionStorage.getItem('returnTo');
            if (returnTo && returnTo !== location.pathname) {
                sessionStorage.removeItem('returnTo');
                navigate(returnTo, { replace: true });
            }
        }
    }, [isLoading, isAuthenticated, navigate, location.pathname]);

    // Redirect to login if not authenticated
    useEffect(() => {
        if (!isLoading && !isAuthenticated && location.pathname !== '/') {
            sessionStorage.setItem('returnTo', location.pathname + location.search);
            loginWithRedirect();
        }
    }, [isLoading, isAuthenticated, loginWithRedirect, location.pathname, location.search]);

    if (isLoading) {
        return <Spin size="large" style={{ display: 'block', textAlign: 'center', marginTop: '100px' }} />;
    }

    if (!isAuthenticated) {
        return <Landing />;
    }

    return <>{children}</>;
};

const router = createBrowserRouter([
    {
        path: "/join/:id",
        element: <PublicAddPlayer/>,
    },
    {
        path: "/reveal",
        element: <Reveal/>,
    },
    {
        path: "*",
        element: <NotFound/>,
    },
    {
        path: "/",
        element: <ProtectedRoute><Sorteggio/></ProtectedRoute>,
    },
    {
        path: "/sorteggio",
        element: <ProtectedRoute><Sorteggio/></ProtectedRoute>,
    },
    {
        path: "/sorteggio/:id",
        element: <ProtectedRoute><Sorteggio/></ProtectedRoute>,
    },
    {
        path: "/sorteggi",
        element: <ProtectedRoute><Sorteggi/></ProtectedRoute>,
    },
    {
        path: "/privacy",
        element: <PrivacyPolicy/>,
    },
    {
        path: "/terms",
        element: <TermsOfService/>,
    },
    {
        path: "/cookies",
        element: <CookiePolicy/>,
    }
]);

function AppSorteggi() {
    return (
            <>
                <GoogleAnalytics />
                <WebViewBanner />
                <div className="App">
                    <ConfigProvider
                    theme={{
                        token: {
                            colorPrimary: '#e57373',
                            colorSuccess: '#66bb6a',
                            colorInfo: '#42a5f5',
                            colorWarning: '#ffa726',
                            colorError: '#ef5350',
                            borderRadius: 12,
                            colorBgContainer: '#ffffff',
                            colorBgLayout: '#f5f5f5',
                            fontSize: 14,
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                        },
                        components: {
                            Button: {
                                borderRadius: 8,
                                controlHeight: 40,
                                fontSize: 15,
                                fontWeight: 500,
                            },
                            Card: {
                                borderRadius: 16,
                                paddingLG: 20,
                            },
                            Input: {
                                borderRadius: 8,
                                controlHeight: 40,
                            },
                            Select: {
                                borderRadius: 8,
                            },
                            Table: {
                                borderRadius: 12,
                            },
                        },
                    }}
                >
                        <App>
                            <RouterProvider router={router}/>
                        </App>
                    </ConfigProvider>
                </div>
            </>
    );
}

export default AppSorteggi;